import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../stores/useUserStore';
import {
  notificationSettingsService,
  NotificationSettings as NotificationSettingsData,
} from '../services/notificationSettingsService';

// shadcn/ui components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from './ui/card';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from './ToastContainer';

// Lucide React icons
import {
  Bell,
  RefreshCw,
  Save,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Package,
  ShoppingCart,
  Users,
  MessageSquare,
} from 'lucide-react';

const NotificationSettings: React.FC = () => {
  const { translate } = useTranslation();
  const { success } = useToast();
  const { user } = useUserStore();
  const [settings, setSettings] = useState<NotificationSettingsData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user has permission to access notification settings
  const hasPermission = () => {
    if (!user) return false;
    const userRoles = user.roles || [];
    return (
      userRoles.includes('admin') ||
      userRoles.includes('orgAdmin') ||
      userRoles.includes('sysAdmin') ||
      userRoles.includes('storeManager')
    );
  };

  useEffect(() => {
    const checkPermission = () => {
      if (!user) return false;
      const userRoles = user.roles || [];
      return (
        userRoles.includes('admin') ||
        userRoles.includes('orgAdmin') ||
        userRoles.includes('sysAdmin') ||
        userRoles.includes('storeManager')
      );
    };

    if (checkPermission()) {
      loadSettings();
    } else {
      setLoading(false);
      setError('You do not have permission to access notification settings');
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationSettingsService.getSettings();
      setSettings(data);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to load notification settings'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (
    key: keyof NotificationSettingsData,
    value: number | boolean
  ) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      const updated = await notificationSettingsService.updateSettings(
        settings
      );
      setSettings(updated);
      success('Notification settings saved successfully');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to save notification settings'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!settings) return;
    setSettings({
      ...settings,
      criticalThreshold: 5,
      lowThreshold: 20,
      mediumThreshold: 50,
      enabled: true,
      inventoryStockEnabled: true,
      orderNotificationsEnabled: true,
      orderNewEnabled: true,
      orderReadyEnabled: true,
      orderDeliveredEnabled: true,
      orderCancelledEnabled: true,
      staffNotificationsEnabled: true,
      generalNotificationsEnabled: true,
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex justify-center items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasPermission()) {
    return (
      <Card>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              Only administrators and store managers can access notification
              settings.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load notification settings
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const hasThresholdError =
    (settings.criticalThreshold >= settings.lowThreshold &&
      settings.lowThreshold > 0) ||
    (settings.lowThreshold >= settings.mediumThreshold &&
      settings.mediumThreshold > 0);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>
                  {translate('pos.settings.notifications.title') ||
                    'Notification Settings'}
                </CardTitle>
                <CardDescription className="mt-1">
                  {translate('pos.settings.notifications.subtitle') ||
                    'Configure notification preferences for your organization'}
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {translate('pos.settings.notifications.reset') || 'Reset'}
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {translate('common.saving') || 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    {translate('common.save') || 'Save'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Global Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Bell className="h-5 w-5" />
              {translate('pos.settings.notifications.global') ||
                'Global Settings'}
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="notifications-enabled">
                    {translate('pos.settings.notifications.enabled') ||
                      'Enable all notifications'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {translate('pos.settings.notifications.enabledHelp') ||
                      'Master switch to enable or disable all notifications. Individual categories can still be controlled below.'}
                  </p>
                </div>
                <Switch
                  id="notifications-enabled"
                  checked={settings.enabled}
                  onCheckedChange={(checked) =>
                    handleSettingChange('enabled', checked)
                  }
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Inventory Stock Notifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Package className="h-5 w-5" />
              {translate('pos.settings.notifications.inventoryStock') ||
                'Inventory Stock Notifications'}
            </h3>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="inventory-stock-enabled">
                    {translate(
                      'pos.settings.notifications.inventoryStockEnabled'
                    ) || 'Enable inventory stock notifications'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {translate(
                      'pos.settings.notifications.inventoryStockEnabledHelp'
                    ) ||
                      'Receive alerts when inventory levels fall below configured thresholds.'}
                  </p>
                </div>
                <Switch
                  id="inventory-stock-enabled"
                  checked={settings.inventoryStockEnabled ?? true}
                  onCheckedChange={(checked) =>
                    handleSettingChange('inventoryStockEnabled', checked)
                  }
                />
              </div>
            </div>

            {settings.inventoryStockEnabled && (
              <div className="space-y-4 pl-6 border-l-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Critical Threshold */}
                  <div className="space-y-2">
                    <Label htmlFor="critical-threshold">
                      {translate(
                        'pos.settings.notifications.criticalThreshold'
                      ) || 'Critical Threshold'}
                    </Label>
                    <Input
                      id="critical-threshold"
                      type="number"
                      min="0"
                      value={settings.criticalThreshold}
                      onChange={(e) =>
                        handleSettingChange(
                          'criticalThreshold',
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={
                        settings.criticalThreshold >= settings.lowThreshold &&
                        settings.lowThreshold > 0
                          ? 'border-destructive'
                          : ''
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {translate(
                        'pos.settings.notifications.criticalThresholdHelp'
                      ) || 'Alert when stock ≤ this level (CRITICAL priority)'}
                    </p>
                  </div>

                  {/* Low Threshold */}
                  <div className="space-y-2">
                    <Label htmlFor="low-threshold">
                      {translate('pos.settings.notifications.lowThreshold') ||
                        'Low Threshold'}
                    </Label>
                    <Input
                      id="low-threshold"
                      type="number"
                      min="0"
                      value={settings.lowThreshold}
                      onChange={(e) =>
                        handleSettingChange(
                          'lowThreshold',
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={
                        (settings.lowThreshold <= settings.criticalThreshold &&
                          settings.criticalThreshold >= 0) ||
                        (settings.lowThreshold >= settings.mediumThreshold &&
                          settings.mediumThreshold > 0)
                          ? 'border-destructive'
                          : ''
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {translate(
                        'pos.settings.notifications.lowThresholdHelp'
                      ) || 'Alert when stock ≤ this level (HIGH priority)'}
                    </p>
                  </div>

                  {/* Medium Threshold */}
                  <div className="space-y-2">
                    <Label htmlFor="medium-threshold">
                      {translate(
                        'pos.settings.notifications.mediumThreshold'
                      ) || 'Medium Threshold'}
                    </Label>
                    <Input
                      id="medium-threshold"
                      type="number"
                      min="0"
                      value={settings.mediumThreshold}
                      onChange={(e) =>
                        handleSettingChange(
                          'mediumThreshold',
                          parseInt(e.target.value) || 0
                        )
                      }
                      className={
                        settings.mediumThreshold <= settings.lowThreshold &&
                        settings.lowThreshold > 0
                          ? 'border-destructive'
                          : ''
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      {translate(
                        'pos.settings.notifications.mediumThresholdHelp'
                      ) || 'Reference threshold (not used for notifications)'}
                    </p>
                  </div>
                </div>

                {hasThresholdError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {translate(
                        'pos.settings.notifications.thresholdOrderWarning'
                      ) ||
                        'Thresholds must be in order: Critical < Low < Medium'}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <Separator />

          {/* Order Notifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              {translate('pos.settings.notifications.orderNotifications') ||
                'Order Notifications'}
            </h3>

            <div className="space-y-4 pl-6 border-l-2">
              {/* Order Notifications Master Switch */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="order-notifications-enabled">
                    {translate(
                      'pos.settings.notifications.orderNotificationsEnabled'
                    ) || 'Enable all order notifications'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {translate(
                      'pos.settings.notifications.orderNotificationsEnabledHelp'
                    ) || 'Master switch for all order-related notifications.'}
                  </p>
                </div>
                <Switch
                  id="order-notifications-enabled"
                  checked={settings.orderNotificationsEnabled ?? true}
                  onCheckedChange={(checked) =>
                    handleSettingChange('orderNotificationsEnabled', checked)
                  }
                />
              </div>

              {settings.orderNotificationsEnabled && (
                <div className="space-y-3 pl-4 border-l-2">
                  {/* New Order */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="order-new-enabled">
                        {translate('pos.settings.notifications.orderNew') ||
                          'New Order Notifications'}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {translate('pos.settings.notifications.orderNewHelp') ||
                          'Notify when a new order is placed'}
                      </p>
                    </div>
                    <Switch
                      id="order-new-enabled"
                      checked={settings.orderNewEnabled ?? true}
                      onCheckedChange={(checked) =>
                        handleSettingChange('orderNewEnabled', checked)
                      }
                    />
                  </div>

                  {/* Order Ready */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="order-ready-enabled">
                        {translate('pos.settings.notifications.orderReady') ||
                          'Order Ready Notifications'}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {translate(
                          'pos.settings.notifications.orderReadyHelp'
                        ) || 'Notify when an order is marked as ready'}
                      </p>
                    </div>
                    <Switch
                      id="order-ready-enabled"
                      checked={settings.orderReadyEnabled ?? true}
                      onCheckedChange={(checked) =>
                        handleSettingChange('orderReadyEnabled', checked)
                      }
                    />
                  </div>

                  {/* Order Delivered */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="order-delivered-enabled">
                        {translate(
                          'pos.settings.notifications.orderDelivered'
                        ) || 'Order Delivered Notifications'}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {translate(
                          'pos.settings.notifications.orderDeliveredHelp'
                        ) || 'Notify when an order is delivered'}
                      </p>
                    </div>
                    <Switch
                      id="order-delivered-enabled"
                      checked={settings.orderDeliveredEnabled ?? true}
                      onCheckedChange={(checked) =>
                        handleSettingChange('orderDeliveredEnabled', checked)
                      }
                    />
                  </div>

                  {/* Order Cancelled */}
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="order-cancelled-enabled">
                        {translate(
                          'pos.settings.notifications.orderCancelled'
                        ) || 'Order Cancelled Notifications'}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {translate(
                          'pos.settings.notifications.orderCancelledHelp'
                        ) || 'Notify when an order is cancelled'}
                      </p>
                    </div>
                    <Switch
                      id="order-cancelled-enabled"
                      checked={settings.orderCancelledEnabled ?? true}
                      onCheckedChange={(checked) =>
                        handleSettingChange('orderCancelledEnabled', checked)
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Staff Notifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" />
              {translate('pos.settings.notifications.staffNotifications') ||
                'Staff Notifications'}
            </h3>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="staff-notifications-enabled">
                    {translate(
                      'pos.settings.notifications.staffNotificationsEnabled'
                    ) || 'Enable staff notifications'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {translate(
                      'pos.settings.notifications.staffNotificationsEnabledHelp'
                    ) ||
                      'Receive notifications about staff activities, attendance, and shift changes.'}
                  </p>
                </div>
                <Switch
                  id="staff-notifications-enabled"
                  checked={settings.staffNotificationsEnabled ?? true}
                  onCheckedChange={(checked) =>
                    handleSettingChange('staffNotificationsEnabled', checked)
                  }
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* General Notifications */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              {translate('pos.settings.notifications.generalNotifications') ||
                'General Notifications'}
            </h3>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="general-notifications-enabled">
                    {translate(
                      'pos.settings.notifications.generalNotificationsEnabled'
                    ) || 'Enable general notifications'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {translate(
                      'pos.settings.notifications.generalNotificationsEnabledHelp'
                    ) ||
                      'Receive general system notifications and announcements.'}
                  </p>
                </div>
                <Switch
                  id="general-notifications-enabled"
                  checked={settings.generalNotificationsEnabled ?? true}
                  onCheckedChange={(checked) =>
                    handleSettingChange('generalNotificationsEnabled', checked)
                  }
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <ToastContainer />
    </>
  );
};

export default NotificationSettings;
